import { useMemo } from 'react'
import { Container } from '@/components/ui/container'
import { Flexbox } from '@/components/ui/flexbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
	OpsVettingCardBodyContainer,
	OpsVettingCardContainer,
	OpsVettingCardGrid,
	OpsVettingHero,
	OpsVettingQuestionsGrid,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'

// Hero Section Skeleton
const ServiceVettingCriteriaHeroSkeleton = () => {
	return (
		<OpsVettingHero fullWidth justify='space-between'>
			<Skeleton height={30} width={100} />
			<Flexbox align='center' gap={4}>
				<Flexbox align='center' gap={2}>
					<Skeleton height={20} width={20} />
					<Skeleton height={20} width={120} />
				</Flexbox>
				<Skeleton height={38} width={100} />
			</Flexbox>
		</OpsVettingHero>
	)
}

// Host Expertise Section Skeleton
const ServiceVettingCriteriaHostExpertiseSkeleton = () => {
	return (
		<OpsVettingSectionWrapper direction='column' fullWidth gap={6}>
			<Flexbox align='center' fullWidth justify='space-between'>
				<Flexbox align='center' gap={8} style={{ flexShrink: 0 }}>
					<OpsVettingSectionTitle>
						<Skeleton height={24} width={200} />
					</OpsVettingSectionTitle>
					<Flexbox gap={2}>
						<Skeleton height={36} width={36} />
						<Skeleton height={36} width={36} />
						<Skeleton height={36} width={36} />
					</Flexbox>
				</Flexbox>
			</Flexbox>

			<Separator orientation='horizontal' />

			<OpsVettingQuestionsGrid>
				<Flexbox direction='column' fullWidth gap={6}>
					{[1, 2, 3, 4, 5].map((n) => (
						<Flexbox direction='column' fullWidth gap={2} key={n}>
							<Skeleton height={18} width={150} />
							<Skeleton height={20} width='100%' />
						</Flexbox>
					))}
				</Flexbox>

				<Separator orientation='vertical' />

				<Flexbox direction='column' fullWidth gap={6}>
					<Flexbox direction='column' gap={2}>
						<Skeleton height={20} width={180} />
						<Skeleton
							height={56}
							style={{ borderRadius: '50%' }}
							width={56}
						/>
					</Flexbox>

					{[1, 2, 3].map((n) => (
						<Flexbox direction='column' fullWidth gap={2} key={n}>
							<Skeleton height={20} width={150} />
							<Skeleton height={20} width='100%' />
						</Flexbox>
					))}
				</Flexbox>
			</OpsVettingQuestionsGrid>
		</OpsVettingSectionWrapper>
	)
}

// Offerings Section Skeleton
const ServiceVettingCriteriaOfferingsSkeleton = () => {
	const offeringItems = useMemo(
		() => Array.from({ length: 3 }, (_, i) => `offering-skeleton-${i}`),
		[]
	)

	return (
		<OpsVettingSectionWrapper direction='column' fullWidth gap={6}>
			<Flexbox align='center' fullWidth justify='space-between'>
				<OpsVettingSectionTitle>
					<Skeleton height={24} width={200} />
				</OpsVettingSectionTitle>
				<Skeleton height={20} width={140} />
			</Flexbox>

			<Separator orientation='horizontal' />

			<OpsVettingCardGrid>
				{offeringItems.map((key) => (
					<OpsVettingCardContainer
						direction='column'
						gap={2}
						key={key}
					>
						<Flexbox
							align='center'
							fullWidth
							gap={2}
							justify='space-between'
						>
							<Flexbox align='center' gap={8}>
								<Skeleton height={22} width={150} />
								<Flexbox gap={2}>
									<Skeleton height={36} width={36} />
									<Skeleton height={36} width={36} />
									<Skeleton height={36} width={36} />
								</Flexbox>
							</Flexbox>
						</Flexbox>

						<OpsVettingCardBodyContainer>
							<Flexbox direction='column' fullWidth gap={2.5}>
								{[1, 2, 3, 4].map((n) => (
									<Flexbox
										direction='column'
										fullWidth
										gap={2}
										key={n}
									>
										<Skeleton height={20} width={120} />
										<Skeleton height={20} width='100%' />
									</Flexbox>
								))}
							</Flexbox>

							<Skeleton
								height={200}
								style={{
									borderRadius: '8px',
									aspectRatio: '1/1'
								}}
								width='100%'
							/>
						</OpsVettingCardBodyContainer>
					</OpsVettingCardContainer>
				))}
			</OpsVettingCardGrid>
		</OpsVettingSectionWrapper>
	)
}

// Main Skeleton Component for the entire page
export const ServiceVettingCriteriaSkeleton = () => {
	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			<Container fullWidth>
				<ServiceVettingCriteriaHeroSkeleton />
			</Container>

			<Container fullWidth>
				<Flexbox direction='column' fullWidth gap={6}>
					<ServiceVettingCriteriaHostExpertiseSkeleton />
					<ServiceVettingCriteriaOfferingsSkeleton />
				</Flexbox>
			</Container>
		</ScrollArea>
	)
}
